import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { ArrowLeft, Scissors } from 'lucide-react';
import { fetchPackageAdminList } from '@/api/Admin/userPackage';
import { splittingPackage } from '@/api/Admin/adminPackage';

interface PackageItem {
    id: number;
    name: string;
    price: string;
    total_slots: number;
    sold_slots: number;
    remaining_slots: number;
    monthly_commission_rate: string;
    min_profit_rate: string;
    duration_months: number;
    is_active: boolean;
    type: string;
    description: string | null;
    parent?: { id: number; name: string; remaining_slots: number } | null;
}

const PRESETS = [2, 3, 4] as const;

const fmtVND = (v: number) => new Intl.NumberFormat('vi-VN').format(Math.round(v)) + ' đ';

export default function SplitPackagePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [packages, setPackages] = useState<PackageItem[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [selectedId, setSelectedId] = useState('');
    const [splitFactor, setSplitFactor] = useState(2);
    const [isCustom, setIsCustom] = useState(false);
    const [customFactor, setCustomFactor] = useState('');
    const [miniName, setMiniName] = useState('');

    const loadPackages = async () => {
        try {
            const res = await fetchPackageAdminList();
            const all = (res.data.data as unknown as PackageItem[]) || [];

            setPackages(all.filter(p => p.parent == null && p.type !== 'mini' && p.name.toLowerCase() !== 'trader'));
        } catch (error) {
            console.error('Error fetching package list:', error);
        }
    };

    useEffect(() => { loadPackages(); }, []);

    const selected = useMemo(
        () => packages.find(p => String(p.id) === selectedId) ?? null,
        [packages, selectedId]
    );

    const factor = isCustom ? (parseInt(customFactor) || 0) : splitFactor;
    const miniPrice = selected && factor >= 2 ? parseFloat(selected.price) / factor : 0;
    const remaining = selected ? selected.remaining_slots : 0;

    useEffect(() => {
        if (!selected) { setMiniName(''); return; }
        const base = selected.name.split(/[\s\-–]/)[0].toUpperCase();
        const mo = selected.duration_months > 0 ? `-${selected.duration_months}M` : '';
        setMiniName(`${base}${mo}-MINI`);
    }, [selected]);

    const handleSplit = async () => {
        if (!selectedId)      { message.warning(t('plan.selectPackageRequired')); return; }
        if (factor < 2)       { message.warning(t('plan.splitFactorInvalid')); return; }
        if (!miniName.trim()) { message.warning(t('plan.splitNameRequired')); return; }

        setSubmitting(true);
        try {
            await splittingPackage(selectedId, factor, miniName.trim());
            message.success(t('plan.splitSuccess'));
            setSelectedId(''); setSplitFactor(2); setIsCustom(false); setCustomFactor('');
            await loadPackages();
        } catch {
            message.error('Tách gói thất bại, vui lòng thử lại.');
        } finally {
            setSubmitting(false);
        }
    };

    const interestRate = parseFloat(
        selected?.monthly_commission_rate || selected?.min_profit_rate || '0'
    ).toFixed(1);

    return (
        <div className="font-sans">
            <Helmet><title>{t('plan.splitPackage')}</title></Helmet>

            {/* Back link */}
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-5"
            >
                <ArrowLeft className="w-4 h-4" />
                Quay lại danh sách
            </button>

            {/* Page header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
                    <Scissors className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Tách gói đầu tư mini</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Tách slot từ gói TITAN/PIONEER thành các gói nhỏ hơn</p>
                </div>
            </div>

            {/* 2-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

                {/* ── LEFT ── */}
                <div className="lg:col-span-2 space-y-4">

                    {/* Step 1 */}
                    <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                        <div className="flex items-center gap-2.5 mb-5">
                            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Chọn gói đầu tư cha</h2>
                        </div>

                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('plan.selectPackage')} <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedId}
                            onChange={e => setSelectedId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                            <option value="">-- {t('plan.selectPackagePlaceholder')} --</option>
                            {packages.map(pkg => {
                                const rem = pkg.total_slots - (pkg.sold_slots || 0);
                                return (
                                    <option key={pkg.id} value={String(pkg.id)}>
                                        {pkg.name} – {pkg.duration_months} tháng – {fmtVND(parseFloat(pkg.price))}/slot
                                    </option>
                                );
                            })}
                        </select>

                        {selected && (
                            <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 bg-gray-50 dark:bg-gray-900/40 rounded-md border border-gray-200 dark:border-gray-700 p-4">
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Loại gói</p>
                                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 uppercase">
                                        {selected.type || 'STANDARD'}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Lãi suất</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{interestRate}% / tháng</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Mệnh giá slot cha</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{fmtVND(parseFloat(selected.price))}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Slots còn lại</p>
                                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{remaining} slots</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Step 2 */}
                    {selected && (
                        <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                            <div className="flex items-center gap-2.5 mb-5">
                                <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Cấu hình tách slot</h2>
                            </div>

                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('plan.splitFactor')} <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-4 gap-2 mb-1">
                                {PRESETS.map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => { setSplitFactor(n); setIsCustom(false); }}
                                        className={`py-2 rounded-md border text-sm font-medium transition-colors ${
                                            !isCustom && splitFactor === n
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-700'
                                        }`}
                                    >
                                        {n} slots
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setIsCustom(true)}
                                    className={`py-2 rounded-md border text-sm font-medium transition-colors ${
                                        isCustom
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-700'
                                    }`}
                                >
                                    Tùy chỉnh
                                </button>
                            </div>

                            {isCustom && (
                                <input
                                    type="number"
                                    min={2}
                                    value={customFactor}
                                    onChange={e => setCustomFactor(e.target.value)}
                                    placeholder="Nhập số slots (tối thiểu 2)"
                                    className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            )}

                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 mb-4">
                                Slot cha sẽ được chia đều thành {factor >= 2 ? factor : '?'} slots nhỏ
                            </p>

                            {/* Calculated price */}
                            <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Mệnh giá mỗi slot mini</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            = {fmtVND(parseFloat(selected.price))} ÷ {factor >= 2 ? factor : '?'}
                                        </p>
                                    </div>
                                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                        {factor >= 2 ? fmtVND(miniPrice) : '—'}
                                    </p>
                                </div>
                            </div>

                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('plan.newPackageName')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={miniName}
                                onChange={e => setMiniName(e.target.value)}
                                placeholder={t('plan.newPackageNamePlaceholder')}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                        </div>
                    )}
                </div>

                {/* ── RIGHT ── */}
                <div className="space-y-4">

                    {/* Actions */}
                    <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4 space-y-2">
                        <button
                            type="button"
                            onClick={handleSplit}
                            disabled={submitting || !selected || factor < 2}
                            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors flex items-center justify-center gap-2"
                        >
                            <Scissors className="w-4 h-4" />
                            {submitting ? t('common.loading') : t('plan.splitNow')}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="w-full py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md font-medium text-sm transition-colors"
                        >
                            {t('common.cancel', 'Hủy bỏ')}
                        </button>
                    </div>

                    {selected ? (
                        <>
                            {/* Diagram */}
                            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Sơ đồ tách slot</h3>

                                {/* Parent */}
                                <div className="border border-gray-200 dark:border-gray-600 rounded-md p-3 mb-2 bg-gray-50 dark:bg-gray-900/40">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Gói cha</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5 leading-snug">{selected.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Slot #1 / {selected.total_slots} slots
                                    </p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{fmtVND(parseFloat(selected.price))}</p>
                                </div>

                                <div className="flex justify-center my-2 text-gray-400">
                                    <Scissors className="w-4 h-4" />
                                </div>

                                {/* Mini */}
                                <div className="border border-gray-200 dark:border-gray-600 rounded-md p-3 mb-3 bg-gray-50 dark:bg-gray-900/40">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Gói mini</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{miniName || '—'}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{factor >= 2 ? factor : '?'} slots nhỏ</p>
                                    <p className="text-xs text-gray-700 dark:text-gray-300 mt-0.5">
                                        {factor >= 2 ? fmtVND(miniPrice) : '—'} / slot
                                    </p>
                                </div>

                                {factor >= 2 && (
                                    <div className="grid grid-cols-2 gap-2">
                                        {Array.from({ length: Math.min(factor, 4) }).map((_, i) => (
                                            <div key={i} className="border border-gray-200 dark:border-gray-600 rounded-md p-2 text-center bg-white dark:bg-gray-700">
                                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Slot {i + 1}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{fmtVND(miniPrice)}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {factor > 4 && (
                                    <p className="text-xs text-center text-gray-400 mt-2">+ {factor - 4} slots nữa</p>
                                )}
                            </div>

                            {/* Summary */}
                            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Tóm tắt</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Gói cha:</span>
                                        <span className="font-medium text-gray-900 dark:text-white text-right max-w-[55%] truncate">{selected.name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Giá trị slot cha:</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{fmtVND(parseFloat(selected.price))}</span>
                                    </div>
                                    <div className="border-t border-gray-100 dark:border-gray-700 pt-2 space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">Số slots mini:</span>
                                            <span className="font-semibold text-blue-600 dark:text-blue-400">{factor >= 2 ? factor : '—'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">Mệnh giá/slot:</span>
                                            <span className="font-semibold text-gray-900 dark:text-white">
                                                {factor >= 2 ? fmtVND(miniPrice) : '—'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">Lãi suất:</span>
                                            <span className="font-medium text-gray-900 dark:text-white">{interestRate}%</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">Thời hạn:</span>
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {selected.duration_months > 0 ? `${selected.duration_months} tháng` : 'Không giới hạn'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Mechanism info */
                        <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Cơ chế hoạt động</h3>
                            <ul className="space-y-2">
                                {[
                                    'Slot cha sẽ bị trừ đi sau khi tách',
                                    'Gói mini kế thừa lãi suất & thời hạn',
                                    'Mệnh giá được chia đều tự động',
                                    'Gói mini có thể bán độc lập',
                                ].map(item => (
                                    <li key={item} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <span className="mt-1 w-1 h-1 rounded-full bg-gray-400 shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
